import { FacilityInstructorSerializer } from 'api/Serializers/Facilities';
import CartAvailabilitySelect from 'components/availability-select';
import Avatar from 'components/avatar';
import Link from 'components/link';
import { ArrowForwardIcon, MessageMiniFilledIcon, ThumbIcon } from 'icons';
import React from 'react';

interface Props extends FacilityInstructorSerializer {
  showAvailability?: boolean;
  to?: string;
  onClick?(): void;
}

const InstructorCard = ({
  avatar,
  basePrice,
  displayName,
  shortDescription,
  numRecommended,
  numDelivered,
  numBookable,
  segments,
  specialization1,
  specialization2,
  showAvailability = false,
  onClick,
  ...rest
}: Props) => {
  const specializations = [specialization1, specialization2].filter(
    (spec) => !!spec
  );
  const canShowAvailability = showAvailability && numBookable > 0;
  const badgeClasses =
    'text-xs items-center gap-1 flex px-1.5 text-gray-100 bg-gray-900 rounded-full py-1 leading-none';

  const body = (
    <div>
      <div className="space-y-3">
        <div className="flex gap-2">
          <div>
            <Avatar src={avatar} diameter={32} variant="rounded" />
          </div>
          <div className="pt-1 space-y-1 grow">
            <div className="flex justify-between">
              <h3 className="my-0 font-bold text-gray-900 text-md">
                {displayName}
              </h3>
              <div className="flex h-6 gap-1">
                {rest.allowUnbookedMessaging && (
                  <div className={badgeClasses}>
                    <MessageMiniFilledIcon fontSize="inherit" width={12} />{' '}
                  </div>
                )}
                {numRecommended > 2 && (
                  <div className={badgeClasses}>
                    <ThumbIcon fontSize="inherit" width={14} fill="#ffffff" />{' '}
                    <span>{numRecommended}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between gap-2">
              <div className="text-sm font-normal text-gray-800">
                <div className="">
                  {segments.concat(specializations).join(' · ')}
                </div>
              </div>
              <ArrowForwardIcon className="shrink-0" width={24} />
            </div>
          </div>
        </div>
        <div className="flex justify-between px-3 text-xs font-normal text-gray-700">
          <div className="">
            <div className="flex justify-center space-x-2 font-bold text-md">
              <span>{numBookable}</span>
            </div>
            <div className="text-xs text-gray-700">Open spots</div>
          </div>
          <div className="">
            <div className="flex justify-center space-x-2 font-bold text-md">
              <span>{numDelivered}</span>
            </div>
            <div className="text-xs text-gray-700">Lessons delivered</div>
          </div>
          <div className="">
            <div className="flex justify-center space-x-2 font-bold text-md">
              ${basePrice}
            </div>
            <div className="text-xs text-gray-700">per lesson</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="flex flex-col gap-3 w-full p-3 overflow-hidden transition-all duration-150 bg-white border border-gray-400 shadow-sm rounded-2xl">
      {safe(rest.to) ? <Link to={rest.to}>{body}</Link> : body}
      {canShowAvailability && (
        <div className="flex flex-col gap-3">
          <hr className="m-0" />
          <CartAvailabilitySelect
            appointmentProductId={rest.appointmentProductId}
            showCartTime={false}
            defaultView={null}
          />
        </div>
      )}
    </section>
  );
};

export default InstructorCard;
